import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { StateType } from '../index';
import { competitionEnrolledCompetitionsSelector } from '../CompetitionsReducer/CompetitionsSelector';
import { Competition, CompetitionImmutable } from '../../components/Competitions/CompetitionsTypes';
import { CountryOptions } from './AccountReducer.types';

export const accountIsFetchingSelector = (state: StateType) => state.getIn(['account', 'isFetching']);
export const accountTermsAcceptedSelector = (state: StateType) => state.getIn(['account', 'termsAccepted']);
export const accountIsValidToSubmitSelector = (state: StateType) => state.getIn(['account', 'isValidToSubmit']);
export const accountChangeEmailValidSelector = (state: StateType) => state.getIn(['account', 'changeEmailForm', 'changeEmail', 'valid']);
export const accountWorkdayPasswordValidSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'workdayPassword', 'valid']);
export const accountBrainLabsPasswordValidSelector = (state: StateType) =>
  state.getIn(['account', 'dataFields', 'brainLabsPassword', 'valid']);
export const accountFetchingTokenSelector = (state: StateType) => state.getIn(['account', 'fetchingToken']);
export const accountDataFieldsMetadataSelector = (state: StateType) => state.getIn(['account', 'metadata']);
export const accountSubmittedSelector = (state: StateType) => state.getIn(['account', 'submitted']);
export const accountLockedSelector = (state: StateType) => state.getIn(['account', 'locked']);
export const accountApprovedSelector = (state: StateType) => state.getIn(['account', 'approved']);

export const accountUserOptionsFailureSelector = (state: StateType) => state.getIn(['account', 'userOptionsFailure']);
export const accountHasUserOptionsSelector = (state: StateType) => state.getIn(['account', 'hasUserOptions']);
export const accountUpdateFormSelector = (state: StateType) => state.getIn(['account', 'updateForm']);
export const accountTermsSelector = (state: StateType) => state.getIn(['account', 'terms']);
export const accountVerifiedSelector = (state: StateType) => state.getIn(['account', 'verified']);
export const accountDirtyFieldsSelector = (state: StateType) => state.getIn(['account', 'dirtyFields']) as Immutable.List<string>;
export const accountIsAccountDetailsAgreeSelector = (state: StateType) => state.getIn(['account', 'isAccountDetailsAgree']);
export const accountRecertificationProgressSelector = (state: StateType) => state.getIn(['account', 'recertificationProgress']);

export const accountIsFetchUserOptionsSelector = (state: StateType) => state.getIn(['account', 'isFetchUserOptions']);
export const accountDataFieldsSelector = (state: StateType) => state.getIn(['account', 'dataFields']) as Immutable.Map<string, any>;
export const accountAccessTokenSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'accessToken', 'value']);
export const accountDataFieldsCountrySelector = (state: StateType) => state.getIn(['account', 'dataFields', 'country', 'value']);

export const accountAccessTokenPasswordSelector = (state: StateType) =>
  state.getIn(['account', 'dataFields', 'accessTokenPassword', 'value']);

export const accountAgreeCompetitionSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'agreeCompetition', 'value']);
export const accountErrorsAgreeCompetitionSelector = (state: StateType) =>
  state.getIn(['account', 'dataFields', 'agreeCompetition', 'errors']);
export const accountValidAgreeCompetitionSelector = (state: StateType) =>
  state.getIn(['account', 'dataFields', 'agreeCompetition', 'valid']);

export const accountEmployedSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'presently_employed', 'value']);
export const accountFinanceSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'finance', 'value']);

export const accountReferrerSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'referrer']);

export const accountErrorsAgreeSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'agree', 'errors']);
export const accountAgreeSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'agree', 'value']);
export const accountAgreePrivacyPolicySelector = (state: StateType) =>
  state.getIn(['account', 'dataFields', 'agreePrivacyPolicy', 'value']);
export const accountErrorsAgreePrivacyPolicySelector = (state: StateType) =>
  state.getIn(['account', 'dataFields', 'agreePrivacyPolicy', 'errors']);

export const accountErrorsPasswordSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'password', 'errors']);
export const accountPasswordSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'password', 'value']);
export const accountErrorsConfirmationSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'confirmation', 'errors']);
export const accountConfirmationSelector = (state: StateType) => state.getIn(['account', 'dataFields', 'confirmation', 'value']);

export const accountAccountVerifyEmail500Selector = (state: StateType) => state.getIn(['account', 'accountVerifyEmail500']);
export const accountVerifiedTypeSelector = (state: StateType) => state.getIn(['account', 'verifiedType']);
export const accountChangedEmailSelector = (state: StateType) => state.getIn(['account', 'changedEmail']);
export const accountUnverifiedEmailSelector = (state: StateType) => state.getIn(['account', 'unverifiedEmail']);

export const accountCountryMapSelector = (state: StateType) => state.getIn(['account', 'metadata', 'country']) as CountryOptions;

export const accountIsAgreedAlphathonUpdateAccountDetails = (state: StateType) =>
  state.getIn(['account', 'isAgreedAlphathonUpdateAccountDetails']) as boolean;

export const accountInvalidCompetitions = createSelector(
  competitionEnrolledCompetitionsSelector,
  accountDirtyFieldsSelector,
  accountDataFieldsSelector,
  (enrolledCompetitions, dirtyFields, dataFields) => {
    const invalidCompetitionsByField: Record<string, CompetitionImmutable[]> = {};
    const mapFieldToCompetitionRestrictionField: Record<string, keyof Competition> = {
      country: 'countries',
      university: 'universities',
    };

    dirtyFields.forEach(fieldName => {
      const restrictionFieldName = mapFieldToCompetitionRestrictionField[fieldName];
      if (!restrictionFieldName) {
        return;
      }

      enrolledCompetitions.forEach(competition => {
        const restriction = competition.get(restrictionFieldName);
        if (!restriction) {
          return;
        }

        const fieldValue = dataFields.getIn([fieldName, 'value']);
        if (!restriction.includes(fieldValue)) {
          (invalidCompetitionsByField[fieldName] ??= []).push(competition);
        }
      });
    });

    return invalidCompetitionsByField;
  }
);
